// ─────────────────────────────────────────────────────────────────────────────
// DeepLinkService
// Escuta central de deep links (Universal Links iOS / App Links Android / custom
// scheme orbitta://). Guia oficial: https://capacitorjs.com/docs/guides/deep-links
//
// Responsabilidades:
//  1. Registrar a escuta do evento `appUrlOpen` (link recebido com app aberto/background).
//  2. Capturar o link de cold-start via `App.getLaunchUrl()` (app aberto pelo link).
//  3. Quebrar a URL inteira em partes úteis (scheme, host, path, segments, params, fragment).
//  4. Logar tudo (debug) e emitir um `DeepLinkPayload` para quem quiser reagir no futuro.
//  5. Navegar de forma SEGURA (somente para rotas conhecidas do app) e SEGURAR essa
//     navegação até o usuário sair dela (ver "Prioridade" abaixo).
//
// Navegação automática é conservadora de propósito: o site oficial é de marketing e
// seus paths NÃO mapeiam 1:1 com as rotas do app. Por isso só navegamos quando o
// primeiro segmento do path bate com uma rota real (KNOWN_ROUTE_PREFIXES). Todo o
// resto é apenas logado + emitido — pronto para regras de negócio futuras.
//
// Prioridade sobre os redirects de startup
// ────────────────────────────────────────
// Em cold start o AuthGuard já está rodando quando o link chega, e o redirect dele
// (`/login` / `/intro`) sai de uma Promise solta que pode aterrissar DEPOIS da nossa
// navegação, roubando a tela do usuário. Por isso o deep link "reivindica" o destino
// (`claimPriority`) e o AuthGuard cede enquanto `isNavigating()` for true.
//
// A reivindicação NÃO é um cronômetro curto: em produção o guard só decide o redirect
// depois de consultar a loja (`getAppUpdateInfo`, timeout de 8s), ler storage, pedir
// biometria (tempo do usuário!) e tentar refresh — facilmente 5–15s. Uma janela de
// poucos segundos expira antes disso e o link é perdido. A reivindicação vale até o
// usuário sair do destino (ou até o teto de segurança `DEEPLINK_PRIORITY_MAX_MS`).
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable, NgZone } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { App, URLOpenListenerEvent } from "@capacitor/app";
import { filter } from "rxjs/operators";
import { Subject } from "rxjs";

import { dlLog } from "./deeplink-debug.logger";
import { KNOWN_WEB_HOSTS, parseDeepLinkUrl } from "./deeplink.parser";
import {
  isRecoveryPasswordDeepLink,
  maskRecoveryUsername,
  matchRecoveryPasswordDeepLink,
  RECOVERY_PASSWORD_ROUTE,
} from "./deeplink.recovery-password";
import { DeepLinkOrigin, DeepLinkPayload } from "./deeplink.types";

/** Primeiros segmentos de rota que o app reconhece (derivados de app.routes.ts /
 *  tabs.routes.ts). Só navegamos automaticamente quando o path começa por um destes. */
const KNOWN_ROUTE_PREFIXES = new Set<string>([
  "tabs",
  "login",
  "register",
  "recovery-password",
  "intro",
  "walkthrough",
  "notifications",
  "qrcode",
  "sfcoke-payment",
  "change-information",
  "blocked-outdated",
  "maintenance",
]);

/** Primeiros segmentos protegidos por AuthGuard (ver tabs.routes.ts). Deep link para
 *  eles NÃO reivindica prioridade: o guard precisa continuar livre para mandar o
 *  usuário deslogado ao login — senão o app ficaria preso numa navegação recusada.
 *  Todas as demais rotas de `app.routes.ts` são públicas (sem guard). */
const GUARDED_ROUTE_PREFIXES = new Set<string>(["tabs"]);

/** Liga/desliga a navegação automática. Mantido como const para fácil ajuste futuro. */
const DEEPLINK_AUTO_NAVIGATE = true;

/**
 * Teto absoluto da reivindicação de prioridade. Rede de segurança: se por algum
 * motivo o destino nunca aterrissar nem for abandonado, o app volta ao normal em
 * vez de ficar com os redirects de startup bloqueados para sempre.
 */
const DEEPLINK_PRIORITY_MAX_MS = 30000;

/**
 * Janela para descartar a MESMA URL entregue duas vezes.
 *
 * No cold start o Capacitor entrega o link por DOIS caminhos: o evento `appUrlOpen`
 * retido (`retainUntilConsumed`, disparado quando o JS registra o listener) e o
 * `App.getLaunchUrl()`. Sem esta trava o app navegaria duas vezes e emitiria
 * `deepLink$` em duplicidade — o que quebraria regras de negócio futuras
 * (cupom aplicado 2x, evento de analytics duplicado, etc.).
 */
const DEEPLINK_DUPLICATE_WINDOW_MS = 5000;

@Injectable({ providedIn: "root" })
export class DeepLinkService {
  /** Emite sempre que um deep link é recebido. Assine para reagir no futuro. */
  public readonly deepLink$ = new Subject<DeepLinkPayload>();

  /** Último deep link recebido nesta sessão (útil para debug/inspeção). */
  public lastDeepLink?: DeepLinkPayload;

  private initialized = false;

  /** Path (sem query/fragment) reivindicado pelo deep link. null = sem reivindicação. */
  private claimedPath: string | null = null;

  /** Teto de tempo da reivindicação atual (ver DEEPLINK_PRIORITY_MAX_MS). */
  private claimExpiresAt = 0;

  /** true depois que a navegação do deep link efetivamente aterrissou no destino. */
  private claimLanded = false;

  /** Última URL tratada + quando — usados para descartar a entrega dupla do cold start. */
  private lastHandledUrl?: string;
  private lastHandledAt = 0;

  constructor(private readonly router: Router, private readonly zone: NgZone) {
    // Assinatura criada já na construção (e não em initialize()): o AuthGuard injeta
    // este serviço no startup e a reivindicação precisa ser liberada assim que o
    // usuário sair do destino, independentemente da ordem de inicialização.
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.onNavigationEnd(event.urlAfterRedirects));
  }

  /**
   * Ponto único de inicialização — chamado no start do app (AppComponent).
   * Idempotente: registrar duas vezes não duplica listeners.
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      dlLog.warn("initialize() chamado novamente — ignorando (listener já ativo)");
      return;
    }
    this.initialized = true;

    dlLog.info("Inicializando escuta de deep links…", {
      hostsVerificados: Array.from(KNOWN_WEB_HOSTS),
      autoNavegar: DEEPLINK_AUTO_NAVIGATE,
    });

    // 1) App aberto/background recebendo um link (fluxo mais comum).
    //    Também é por aqui que o cold start do Android chega primeiro: o Capacitor
    //    dispara `appUrlOpen` retido no launch e entrega assim que o listener existe.
    try {
      await App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
        // O evento chega FORA da zona do Angular → navegação/emissão precisam do zone.run.
        this.zone.run(() => this.handleIncomingUrl(event.url, "runtime"));
      });
      dlLog.ok("Listener 'appUrlOpen' registrado");
    } catch (error) {
      dlLog.error("Falha ao registrar listener 'appUrlOpen'", error);
    }

    // 2) App aberto DO ZERO por um link (cold start).
    try {
      const launch = await App.getLaunchUrl();
      if (launch?.url) {
        dlLog.info("App iniciado por um link (cold start)");
        this.zone.run(() => this.handleIncomingUrl(launch.url, "cold-start"));
      } else {
        dlLog.info("Nenhum launch URL — app aberto normalmente (sem deep link)");
      }
    } catch (error) {
      dlLog.error("Falha ao consultar App.getLaunchUrl()", error);
    }
  }

  /**
   * Núcleo do tratamento: recebe a URL inteira, quebra em partes, loga, emite e
   * (quando seguro) navega. É o "gancho" pronto para tratamentos futuros de params.
   */
  public openNotificationUrl(url: string): void {
    this.zone.run(() => this.handleIncomingUrl(url, "runtime"));
  }

  private handleIncomingUrl(url: string, origin: DeepLinkOrigin): void {
    dlLog.info(`Deep link recebido (${origin})`, { url });

    // Cold start entrega o mesmo link duas vezes (appUrlOpen retido + getLaunchUrl).
    if (this.isDuplicateDelivery(url)) {
      dlLog.info(`Entrega duplicada do mesmo link ignorada (${origin})`, {
        url,
        janelaMs: DEEPLINK_DUPLICATE_WINDOW_MS,
      });
      return;
    }

    let payload: DeepLinkPayload;
    try {
      payload = parseDeepLinkUrl(url, origin);
    } catch (error) {
      dlLog.error("Não foi possível interpretar a URL do deep link", error, { url });
      return;
    }

    // Log completo e legível de TODAS as partes — o que o dev pediu para acompanhar.
    dlLog.ok("Deep link interpretado", {
      origem: payload.origin,
      urlCompleta: payload.url,
      scheme: payload.scheme,
      host: payload.host || "(custom scheme)",
      linkWebVerificado: payload.isVerifiedWebLink,
      path: payload.path,
      segmentos: payload.segments,
      params: payload.params,
      totalParams: Object.keys(payload.params).length,
      fragment: payload.fragment || "(nenhum)",
      rotaResolvida: payload.route,
    });

    // Emite para assinantes (regras de negócio futuras: cupom, promo, etc.).
    this.lastDeepLink = payload;
    try {
      this.deepLink$.next(payload);
    } catch (error) {
      dlLog.error("Erro ao emitir deepLink$ para assinantes", error);
    }

    // Navegação segura (opcional).
    this.maybeNavigate(payload);
  }

  /**
   * true enquanto o destino de um deep link está reivindicado — ou seja, entre a
   * chegada do link e o momento em que o usuário sai daquela tela.
   *
   * Usado pelo AuthGuard (`navigateIfNoDeepLink`) para não sobrescrever o destino do
   * link com o redirect padrão de startup.
   */
  public isNavigating(): boolean {
    if (!this.claimedPath) {
      return false;
    }
    if (Date.now() >= this.claimExpiresAt) {
      this.releaseClaim("teto de tempo da prioridade atingido");
      return false;
    }
    return true;
  }

  /** Navega apenas quando é seguro (primeiro segmento é uma rota conhecida). */
  private maybeNavigate(payload: DeepLinkPayload): void {
    if (!DEEPLINK_AUTO_NAVIGATE) {
      dlLog.info("Navegação automática desligada — apenas emitido/logado");
      return;
    }

    // Regra dedicada: recuperação de senha. Precisa vir ANTES da navegação genérica
    // porque a rota é reconstruída de forma canônica (`?code=…&username=…`), sem
    // depender de como o link veio escrito.
    if (this.handleRecoveryPasswordLink(payload)) {
      return;
    }

    const firstSegment = payload.segments[0];

    // Link para a raiz do site (sem path): nada a navegar, o app só precisa abrir.
    if (!firstSegment) {
      dlLog.info("Link sem path (raiz) — app aberto, sem navegação", {
        params: payload.params,
      });
      return;
    }

    if (!KNOWN_ROUTE_PREFIXES.has(firstSegment)) {
      dlLog.warn(
        "Path não corresponde a uma rota conhecida do app — sem navegação automática (aguardando regra futura)",
        { path: payload.path, primeiroSegmento: firstSegment }
      );
      return;
    }

    this.navigate(payload.route, "rota do deep link", !GUARDED_ROUTE_PREFIXES.has(firstSegment));
  }

  /**
   * Recuperação de senha via link (`/recovery-password?code=…&username=…`).
   *
   * Mesmo comportamento do site: a página entra direto no passo "definir nova senha"
   * com o código e o usuário já preenchidos. A URL é REMONTADA de forma canônica para
   * que a página encontre exatamente os nomes de parâmetro que ela lê, mesmo que o
   * link tenha chegado com `?Code=`/`?cpfCnpj=`.
   *
   * @returns true quando o link é de recuperação de senha (tratado aqui, não segue
   *          para a navegação genérica).
   */
  private handleRecoveryPasswordLink(payload: DeepLinkPayload): boolean {
    if (!isRecoveryPasswordDeepLink(payload)) {
      return false;
    }

    const recovery = matchRecoveryPasswordDeepLink(payload);

    // Link de recuperação sem code/username: abre a página no passo 1 (pedindo o
    // CPF/CNPJ) em vez de descartar — é o que o site faz.
    if (!recovery) {
      dlLog.warn("Link de recuperação de senha sem code/username — abrindo a página no passo inicial", {
        params: payload.params,
      });
      this.navigate(RECOVERY_PASSWORD_ROUTE, "recuperação de senha (sem parâmetros)", true);
      return true;
    }

    dlLog.ok("Deep link de recuperação de senha reconhecido", {
      origem: payload.origin,
      code: recovery.code,
      username: maskRecoveryUsername(recovery.username),
    });

    const target =
      `${RECOVERY_PASSWORD_ROUTE}` +
      `?code=${encodeURIComponent(recovery.code)}` +
      `&username=${encodeURIComponent(recovery.username)}`;

    this.navigate(target, "recuperação de senha", true);
    return true;
  }

  /**
   * Navegação única do módulo: loga o desfecho e, quando pedido, reivindica a
   * prioridade sobre os redirects de startup.
   *
   * `claimPriority` só vale para destinos PÚBLICOS (sem AuthGuard) — ver
   * GUARDED_ROUTE_PREFIXES.
   */
  private navigate(url: string, motivo: string, claimPriority: boolean): void {
    if (claimPriority) {
      this.claimPriority(url);
    }
    dlLog.info(`Navegando para ${motivo}`, { rota: url, prioridade: claimPriority });

    this.router
      .navigateByUrl(url)
      .then((ok) => {
        if (ok) {
          dlLog.ok("Navegação concluída", { rota: url });
          return;
        }
        dlLog.warn("Router recusou a navegação (guard/rota inválida?)", { rota: url });
        // Só devolve a prioridade se o destino NUNCA aterrissou. Um `false` depois de
        // aterrissar é só o Angular ignorando navegação para a mesma URL — nesse caso
        // a tela correta já está no ar e a prioridade precisa continuar valendo.
        if (claimPriority && !this.claimLanded) {
          this.releaseClaim("router recusou a navegação");
        }
      })
      .catch((error) => {
        dlLog.error("Erro ao navegar para o deep link", error, { rota: url });
        if (claimPriority && !this.claimLanded) {
          this.releaseClaim("erro ao navegar");
        }
      });
  }

  /** Marca o destino do deep link como dono da tela até o usuário sair dele. */
  private claimPriority(url: string): void {
    const target = pathOf(url);
    // Reivindicar o MESMO destino de novo (ex.: usuário tocou no link já estando na
    // tela) apenas renova o teto — não pode esquecer que a tela já está no ar, senão
    // o `false` do Angular para navegação repetida devolveria a prioridade à toa.
    if (this.claimedPath !== target) {
      this.claimLanded = false;
    }
    this.claimedPath = target;
    this.claimExpiresAt = Date.now() + DEEPLINK_PRIORITY_MAX_MS;
    dlLog.info("Prioridade reivindicada — redirects de startup vão ceder", {
      destino: this.claimedPath,
      tetoMs: DEEPLINK_PRIORITY_MAX_MS,
    });
  }

  private releaseClaim(motivo: string): void {
    if (!this.claimedPath) {
      return;
    }
    dlLog.info("Prioridade do deep link liberada", { destino: this.claimedPath, motivo });
    this.claimedPath = null;
    this.claimLanded = false;
    this.claimExpiresAt = 0;
  }

  /**
   * Acompanha as navegações que realmente aterrissam:
   *  - chegou no destino reivindicado → marca como aterrissado (prioridade segue valendo);
   *  - saiu do destino depois de aterrissar → o usuário seguiu o fluxo, libera a prioridade.
   */
  private onNavigationEnd(url: string): void {
    if (!this.claimedPath) {
      return;
    }

    const path = pathOf(url);
    if (path === this.claimedPath) {
      if (!this.claimLanded) {
        this.claimLanded = true;
        dlLog.ok("Deep link aterrissou no destino", { rota: url });
      }
      return;
    }

    if (this.claimLanded) {
      this.releaseClaim(`usuário saiu do destino (agora em ${path})`);
    }
  }

  /**
   * true quando a MESMA URL já foi tratada há pouco (entrega dupla do cold start).
   * Sempre registra a URL/horário — inclusive quando é duplicata — para que três
   * entregas seguidas do mesmo link também sejam absorvidas.
   */
  private isDuplicateDelivery(url: string): boolean {
    const now = Date.now();
    const duplicate = this.lastHandledUrl === url && now - this.lastHandledAt < DEEPLINK_DUPLICATE_WINDOW_MS;
    this.lastHandledUrl = url;
    this.lastHandledAt = now;
    return duplicate;
  }
}

/** Path puro de uma URL do Router (`/recovery-password?code=1#x` → `/recovery-password`). */
function pathOf(url: string): string {
  return url.split("?")[0].split("#")[0];
}
